import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

// Percentile colors - gradient from good to critical
const PERCENTILE_CONFIG = {
  p50: { label: 'p50 (Median)', color: '#32CD32', description: '50% faster than this' },
  p75: { label: 'p75', color: '#00BCD4', description: '75% faster than this' },
  p95: { label: 'p95', color: '#F59E0B', description: '95% faster than this' },
  p99: { label: 'p99', color: '#EF4444', description: '99% faster than this' }
};

/**
 * PerformanceChart - Displays latency percentiles as horizontal progress bars
 * Clean, scannable visualization for performance analysis
 */
const PerformanceChart = ({ runs, loading }) => {
  // Calculate percentiles
  const { percentiles, maxLatency, avgLatency } = useMemo(() => {
    if (!runs || runs.length === 0) {
      return { percentiles: null, maxLatency: 0, avgLatency: 0 };
    }
    
    const latencies = runs
      .map(r => r.latency_ms || 0)
      .filter(l => l > 0)
      .sort((a, b) => a - b);
    
    if (latencies.length === 0) {
      return { percentiles: null, maxLatency: 0, avgLatency: 0 };
    }
    
    const getPercentile = (arr, p) => {
      const index = Math.ceil((p / 100) * arr.length) - 1;
      return arr[Math.max(0, index)];
    };
    
    const p50 = getPercentile(latencies, 50);
    const p75 = getPercentile(latencies, 75);
    const p95 = getPercentile(latencies, 95);
    const p99 = getPercentile(latencies, 99);
    const max = latencies[latencies.length - 1];
    const avg = latencies.reduce((a, b) => a + b, 0) / latencies.length;
    
    return {
      percentiles: { p50, p75, p95, p99 },
      maxLatency: max,
      avgLatency: avg
    };
  }, [runs]);

  // Format latency for display
  const formatLatency = (ms) => {
    if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  if (loading && (!runs || runs.length === 0)) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <div className="h-4 w-40 rounded mb-4 animate-pulse bg-success-50"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-3 w-16 rounded mb-2 bg-success-50"></div>
              <div className="h-6 rounded bg-primary-500/5"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!percentiles) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-primary-500/25">
        <h3 className="text-sm font-semibold mb-4 text-text-primary">
          Latency Percentiles
        </h3>
        <div className="h-[200px] flex flex-col items-center justify-center">
          <svg className="w-10 h-10 mb-2 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <p className="text-sm text-text-tertiary">No latency data available</p>
        </div>
      </div>
    );
  }

  const percentileEntries = [
    { key: 'p50', value: percentiles.p50 },
    { key: 'p75', value: percentiles.p75 },
    { key: 'p95', value: percentiles.p95 },
    { key: 'p99', value: percentiles.p99 }
  ];

  return (
    <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-primary-500/25">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Latency Percentiles
          </h3>
          <p className="text-xs mt-0.5 text-text-tertiary">
            Avg {formatLatency(avgLatency)} · Max {formatLatency(maxLatency)}
          </p>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold font-mono text-primary-500">
            {formatLatency(percentiles.p50)}
          </span>
          <p className="text-xs text-text-tertiary">
            median
          </p>
        </div>
      </div>
      
      <div className="h-[200px] flex flex-col justify-between">
        {percentileEntries.map(({ key, value }) => {
          const config = PERCENTILE_CONFIG[key];
          const percentage = maxLatency > 0 ? (value / maxLatency) * 100 : 0;
          
          return (
            <div key={key} className="group">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-text-secondary">
                  {config.label}
                </span>
                <span className="text-sm font-mono font-semibold" style={{ color: config.color }}>
                  {formatLatency(value)}
                </span>
              </div>
              <div className="h-3 rounded-full overflow-hidden bg-success-50">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{ 
                    width: `${Math.max(percentage, 2)}%`,
                    background: `linear-gradient(90deg, ${config.color}90, ${config.color})`,
                    boxShadow: `0 0 8px ${config.color}40`
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

PerformanceChart.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.shape({
    latency_ms: PropTypes.number
  })),
  loading: PropTypes.bool
};

export default PerformanceChart;
